import React from 'react';
import { UseFormRegister } from 'react-hook-form';
import { Search, LucideIcon } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';

interface FormInputProps {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  register: UseFormRegister<any>;
  error?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void | Promise<void>;
  showSearchHint?: boolean;
  hintIcon?: LucideIcon;
  hintText?: string;
  onSearch?: () => void | Promise<void>;
}

export default function FormInput({
  label,
  name,
  type = 'text',
  required = false,
  disabled = false,
  placeholder,
  register,
  error,
  onKeyDown,
  showSearchHint = false,
  hintIcon: HintIcon = Search,
  hintText = 'Enter to search',
  onSearch,
}: FormInputProps) {
  const { theme } = useTheme();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      e.preventDefault();
      onSearch();
    }
    onKeyDown?.(e);
  };

  return (
    <div>
      <label className={`block text-sm font-medium ${
        theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
      }`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          type={type}
          {...register(name, { required: required && `${label} is required` })}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={`
            mt-1 block w-full rounded-md px-3 py-2 border border-border
            shadow-sm transition-colors duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-blue-500/20
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500 hover:border-gray-500'
              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500 hover:border-gray-400'
            }
            ${disabled && theme === 'dark'
              ? 'cursor-not-allowed bg-gray-800 text-gray-400'
              : disabled
              ? 'cursor-not-allowed bg-gray-50 text-gray-500'
              : ''
            }
            ${showSearchHint ? 'pr-24' : 'pr-3'}
          `}
        />
        {showSearchHint && (
          <>
            <div className="absolute inset-y-0 right-12 flex items-center pointer-events-none">
              <div className={`flex items-center space-x-1 text-xs ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-400'
              }`}>
                <span>{hintText}</span>
              </div>
            </div>
            {onSearch && (
              <button
                type="button"
                onClick={onSearch}
                className={`absolute inset-y-0 right-0 px-3 flex items-center ${
                  theme === 'dark'
                    ? 'text-gray-400 hover:text-blue-400'
                    : 'text-gray-400 hover:text-blue-500'
                } focus:outline-none`}
              >
                <HintIcon className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm font-medium text-red-500">{error}</p>}
    </div>
  );
}